import React, { useId, useState } from "react";
import { buildCampaignMediaNetwork, formatAllocationPercentLabel } from "../utils/campaignMediaNetwork";
import "./CampaignMediaNetwork.css";

// Deterministic, lightweight geometry: no canvas, timers, random layouts or
// external graphics. The neural sphere remains fully drawn with motion disabled.
function NeuralCore({ id }) {
  const rings = Array.from({ length: 7 }, (_, row) => {
    const latitude = (row + 1) * Math.PI / 8;
    return Array.from({ length: 12 }, (_, col) => {
      const longitude = col * Math.PI / 6 + (row % 2) * Math.PI / 12;
      return {
        x: 140 + Math.sin(latitude) * Math.cos(longitude) * 91,
        y: 130 + Math.cos(latitude) * 90 + Math.sin(longitude) * Math.sin(latitude) * 18,
        front: Math.sin(longitude) >= 0,
      };
    });
  });
  return (
    <svg viewBox="0 0 280 280" className="campaign-media-network__sphere" aria-hidden="true">
      <defs>
        <radialGradient id={`${id}-aura`}>
          <stop stopColor="#9454f9" stopOpacity=".35" />
          <stop offset=".6" stopColor="#6c35d8" stopOpacity=".13" />
          <stop offset="1" stopColor="#6c35d8" stopOpacity="0" />
        </radialGradient>
        <linearGradient id={`${id}-mesh`} x1="0" y1="0" x2="1" y2="1">
          <stop stopColor="#64dfff" /><stop offset=".5" stopColor="#ae7cff" /><stop offset="1" stopColor="#e475e9" />
        </linearGradient>
      </defs>
      <circle cx="140" cy="130" r="128" fill={`url(#${id}-aura)`} />
      <g fill="none" stroke={`url(#${id}-mesh)`}>
        <ellipse cx="140" cy="130" rx="113" ry="111" strokeOpacity=".22" strokeDasharray="2 8" />
        <ellipse cx="140" cy="130" rx="122" ry="45" transform="rotate(-27 140 130)" strokeOpacity=".28" />
        <ellipse cx="140" cy="130" rx="118" ry="39" transform="rotate(37 140 130)" strokeOpacity=".14" />
        {rings.flatMap((ring, row) => ring.map((point, col) => {
          const next = ring[(col + 1) % ring.length];
          const below = rings[row + 1]?.[col];
          const diagonal = rings[row + 1]?.[(col + 1) % ring.length];
          return <path key={`${row}-${col}`} strokeWidth={point.front ? ".9" : ".55"} opacity={point.front ? ".62" : ".18"}
            d={`M${point.x},${point.y} L${next.x},${next.y}${below ? ` M${point.x},${point.y} L${below.x},${below.y} L${diagonal.x},${diagonal.y}` : ""}`} />;
        }))}
      </g>
      {rings.flatMap((ring, row) => ring.filter((p, i) => p.front && (i + row) % 2 === 0).map((point, col) => (
        <circle key={`${row}-${col}`} cx={point.x} cy={point.y} r={col % 3 === 0 ? 2.2 : 1.3} fill={col % 2 ? "#e4b3ff" : "#9ce9ff"} />
      )))}
      <ellipse cx="140" cy="248" rx="60" ry="4" fill="#a266ed" opacity=".13" />
    </svg>
  );
}

function GenericMediaIcon() {
  return <svg viewBox="0 0 40 40" fill="none" stroke="currentColor" strokeWidth="1.4" aria-hidden="true">
    <rect x="7" y="9" width="26" height="22" rx="6" /><path d="m17 15 9 5-9 5z" />
    <path d="M20 4v5m0 22v5M2 20h5m26 0h5" />
  </svg>;
}

function NodeIcon({ node }) {
  const [failedSource, setFailedSource] = useState(null);
  // The registry read already withholds animated artwork; this keeps a legacy
  // GIF from ever bypassing the component's reduced-motion state.
  const stillAsset = node.image && !/\.gif(?:$|[?#])/i.test(node.image);
  return <span className="campaign-media-network__icon" aria-hidden="true">
    {stillAsset && failedSource !== node.image
      ? <img src={node.image} alt="" width="48" height="48" loading="lazy" onError={() => setFailedSource(node.image)} />
      : <GenericMediaIcon />}
  </span>;
}

// A channel's saved role through the page's own advertiser copy pass (the one
// its platform plan cards use), so the two never disagree.
const formatRole = (renderCopy, role) => {
  const copy = typeof renderCopy === "function" ? renderCopy(role || "") : role || "";
  return typeof copy === "string" ? copy.trim() : copy;
};

/** Read-path only: platforms is the exact normalized list used by role cards.
 * Allocations can enrich those nodes, never create a destination.
 * selectedScope retains the saved family/child choice for allocation evidence;
 * a partial detail card cannot stand in for the scope of the whole family.
 */
export default function CampaignMediaNetwork({ platforms = [], allocations = [], selectedScope = platforms, renderCopy = (value) => value }) {
  // Kept in React's own ":r<n>:" form: the page's whole-page controls normalize
  // exactly that form when they compare renders whose earlier sections differ.
  const id = useId();
  const [selectedKey, setSelectedKey] = useState(null);
  const nodes = buildCampaignMediaNetwork(platforms, allocations, selectedScope);
  if (!nodes.length) return null;
  const selected = nodes.find((node) => node.key === selectedKey);
  const roleFallback = "Explore this channel’s strategy in the platform plan below.";
  // The diagram shows each channel's name and share; its role is read in the
  // platform plan card below. Assistive technology hears the same role as the
  // node's description, from an element that is never displayed.
  const roles = nodes.map((node) => formatRole(renderCopy, node.role) || roleFallback);
  const rows = Math.ceil(nodes.length / 2);
  const diagramHeight = Math.max(360, rows * 120);
  const single = nodes.length === 1;
  const hasWeights = nodes.every((node) => node.allocationPercent !== null);
  const maxShare = hasWeights ? Math.max(...nodes.map((node) => node.allocationPercent), 1) : 1;
  const nodePosition = (index) => ({
    x: single ? 78 : index % 2 === 0 ? 16 : 84,
    y: single ? 50 : ((Math.floor(index / 2) + .5) / rows) * 100,
  });
  return (
    <section className={`campaign-media-network${single ? " campaign-media-network--single" : ""}`}
      aria-labelledby={`${id}-title`} data-platform-count={nodes.length}>
      <header className="campaign-media-network__header">
        <div>
          <span className="campaign-media-network__eyebrow">Campaign media network</span>
          <h2 id={`${id}-title`}>The connected media ecosystem</h2>
        </div>
        <span className="campaign-media-network__count"><span aria-hidden="true" />{nodes.length} {single ? "environment" : "environments"}</span>
      </header>
      <p className="campaign-media-network__intro">
        {single
          ? "Cphere connects audience intelligence with the campaign’s selected media environment."
          : `Cphere connects the campaign across ${nodes.length} coordinated media environments, each with a role in the audience journey.`}
      </p>
      <div className="campaign-media-network__diagram" style={{ "--network-height": `${diagramHeight}px`, "--network-rows": rows }}>
        <svg className="campaign-media-network__paths" viewBox={`0 0 1000 ${diagramHeight}`} preserveAspectRatio="none" aria-hidden="true">
          <defs>
            <linearGradient id={`${id}-path`} gradientUnits="userSpaceOnUse" x1="0" y1="0" x2="1000" y2="0"><stop stopColor="#58d9f3" /><stop offset=".5" stopColor="#a281eb" /><stop offset="1" stopColor="#ce73ec" /></linearGradient>
          </defs>
          {nodes.map((node, index) => {
            const position = nodePosition(index);
            const x = position.x * 10;
            const y = position.y * diagramHeight / 100;
            const coreX = single ? 350 : 500;
            const strength = hasWeights ? .5 + .5 * node.allocationPercent / maxShare : .65;
            const d = `M${coreX},${diagramHeight / 2} C${coreX + (x - coreX) * .55},${diagramHeight / 2} ${coreX + (x - coreX) * .55},${y} ${x},${y}`;
            return <g key={node.key} className={selected?.key === node.key ? "is-selected" : ""} style={{ "--path-strength": strength, "--path-delay": `${index * -.9}s` }}>
              <path className="campaign-media-network__path-bloom" d={d} stroke={`url(#${id}-path)`} />
              <path className="campaign-media-network__path" d={d} stroke={`url(#${id}-path)`} />
              <path className="campaign-media-network__pulse" d={d} pathLength="100" stroke={`url(#${id}-path)`} />
            </g>;
          })}
        </svg>
        <div className="campaign-media-network__core">
          <NeuralCore id={id} />
          <div className="campaign-media-network__core-brand"><strong>CPHERE</strong><span>Intelligence core</span></div>
          <span className="campaign-media-network__core-caption">Audience orchestration</span>
        </div>
        <ul className="campaign-media-network__nodes" aria-label="Selected campaign channels">
          {nodes.map((node, index) => (
            <li key={node.key} className="campaign-media-network__node" data-platform-key={node.key}
              style={{ "--node-strength": hasWeights ? .18 + .22 * node.allocationPercent / maxShare : .24 }}>
              <button type="button" className="campaign-media-network__node-button"
                aria-pressed={selected?.key === node.key} aria-describedby={`${id}-role-${index}`}
                onClick={() => setSelectedKey(node.key)} onFocus={() => setSelectedKey(node.key)}
                onMouseEnter={() => setSelectedKey(node.key)}>
                <NodeIcon node={node} />
                <span className="campaign-media-network__node-copy">
                  <span className="campaign-media-network__node-title">{node.label}</span>
                  <span className="campaign-media-network__node-note">{node.allocationPercent !== null ? `${formatAllocationPercentLabel(node.allocationPercent)} planned share` : "Selected channel"}</span>
                </span>
                <span className="campaign-media-network__node-arrow" aria-hidden="true">↗</span>
              </button>
              <span className="campaign-media-network__node-role" id={`${id}-role-${index}`} hidden>{roles[index]}</span>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}
